import { useQuery, useMutation } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { Car, Download, FileSignature, FileText, IdCard, Plane, Ticket } from "lucide-react";
import {
  downloadPortalDocument,
  getPortalDocuments,
  type ArtistDocument,
} from "@/api/client";
import type { PortalOutletContext } from "@/pages/artist-portal/ArtistPortalDetailPage";

const CATEGORY_ICON: Record<string, typeof FileText> = {
  flight_ticket: Plane,
  ground_transport: Car,
  contract: FileSignature,
  itinerary: Ticket,
  id_document: IdCard,
  other: FileText,
};

export function DocumentsTab() {
  const { festivalId } = useOutletContext<PortalOutletContext>();
  const q = useQuery({
    queryKey: ["artist-portal", festivalId, "documents"],
    queryFn: () => getPortalDocuments(festivalId),
  });

  const dl = useMutation({
    mutationFn: (docId: string) => downloadPortalDocument(festivalId, docId),
    onSuccess: (data) => {
      window.open(data.signed_url, "_blank", "noopener,noreferrer");
    },
  });
  // Track which row is currently downloading so the spinner is per-row,
  // not shared across all download buttons.
  const downloadingId = dl.isPending ? (dl.variables as string) : null;

  if (q.isLoading) return <div className="text-ink-3">Loading documents…</div>;
  if (q.isError) return <div className="text-bad">Could not load documents.</div>;

  const docs = q.data ?? [];
  if (docs.length === 0) {
    return (
      <div className="card p-8 text-center text-sm text-ink-3">
        No documents yet. Your festival will upload flight tickets, hotel
        vouchers and other materials here when ready.
      </div>
    );
  }

  // Group by category (preserves insertion order = server order = created_at DESC)
  const byCat = new Map<string, ArtistDocument[]>();
  for (const d of docs) {
    const arr = byCat.get(d.category) ?? [];
    arr.push(d);
    byCat.set(d.category, arr);
  }

  return (
    <div className="space-y-6">
      {[...byCat.entries()].map(([cat, rows]) => {
        const Icon = CATEGORY_ICON[cat] ?? FileText;
        return (
          <section key={cat}>
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ink-3">
              <Icon className="h-4 w-4" /> {cat.replaceAll("_", " ")}
            </h2>
            <ul className="space-y-2">
              {rows.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between card p-3"
                >
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-ink">{d.title}</div>
                    <div className="truncate text-xs text-ink-3">
                      {d.file_name} · {fmtSize(d.file_size_bytes)}
                      {d.description ? ` · ${d.description}` : ""}
                    </div>
                  </div>
                  <button
                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-3 py-1.5 text-sm text-accent hover:bg-panel-2 disabled:opacity-50"
                    disabled={dl.isPending}
                    onClick={() => dl.mutate(d.id)}
                  >
                    {downloadingId === d.id ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
                        Opening…
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" /> Download
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
